import {
  GLOBAL_CRITICAL,
  assetsForPath,
  prefetchTargetsForPath
} from '../config/routeAssets';

const done = new Set();
const inflight = new Map();

function normalizeSrc(src) {
  if (!src || typeof src !== 'string') return '';
  if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
    return src;
  }
  if (src.startsWith('/')) return src;
  return `/${src}`;
}

/**
 * Preload a single image. External URLs supported (team/league CDN logos).
 * @param {'high'|'low'} priority — high uses link rel=preload + fetchPriority
 */
export function preloadImage(src, { priority = 'low' } = {}) {
  const url = normalizeSrc(src);
  if (!url) return Promise.resolve();

  if (done.has(url)) return Promise.resolve();
  if (inflight.has(url)) return inflight.get(url);

  const task = new Promise((resolve) => {
    if (typeof document !== 'undefined' && priority === 'high') {
      const href = url.split('?')[0];
      const exists = document.querySelector(
        `link[rel="preload"][as="image"][href="${url}"], link[rel="preload"][as="image"][href="${href}"]`
      );
      if (!exists) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      }
    }

    const img = new Image();
    img.decoding = 'async';
    if (priority === 'high' && 'fetchPriority' in img) {
      img.fetchPriority = 'high';
    } else if (priority === 'low' && 'fetchPriority' in img) {
      img.fetchPriority = 'low';
    }

    const finish = () => {
      done.add(url);
      resolve();
    };

    img.onload = finish;
    img.onerror = finish;
    img.src = url;
  });

  inflight.set(url, task);
  return task.finally(() => inflight.delete(url));
}

export function preloadImages(urls, options = {}) {
  const list = [...new Set((urls || []).map(normalizeSrc).filter(Boolean))];
  return Promise.all(list.map((url) => preloadImage(url, options)));
}

/** Boot — critical brand assets before React paints routes. */
export function preloadGlobalCritical() {
  return preloadImages(GLOBAL_CRITICAL, { priority: 'high' });
}

/** Route-aware preload: high first, then low in idle time. */
export function preloadForRoute(pathname) {
  const { high, low } = assetsForPath(pathname);

  preloadImages(high, { priority: 'high' });

  const runLow = () => preloadImages(low, { priority: 'low' });
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(runLow, { timeout: 2500 });
  } else {
    setTimeout(runLow, 400);
  }
}

/** Hub pages: warm likely next screens without blocking. */
export function prefetchAdjacentAssets(pathname) {
  const { assets } = prefetchTargetsForPath(pathname);
  if (!assets.length) return;

  const run = () => preloadImages(assets, { priority: 'low' });
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1200);
  }
}

/** Match / list rows — preload logos for upcoming visible items. */
export function preloadExternalUrls(urls, { limit = 12 } = {}) {
  const list = [...new Set((urls || []).filter(Boolean))].slice(0, limit);
  preloadImages(list, { priority: 'low' });
}
